import React from 'react';
import { 
  Clock, Database, TrendingUp, Layers, Settings, 
  Rocket, Calendar, CheckCircle2, Circle, LucideIcon
} from 'lucide-react';

interface ComingSoonProps {
  module: string;
}

interface ModuleConfig {
  icon: LucideIcon;
  accent: string;
  description: string;
  tagline: string;
  eta: string;
  progress: number;
  milestones: Array<{ label: string; done: boolean }>;
  capabilities: string[];
}

const modules: Record<string, ModuleConfig> = {
  Kronos: {
    icon: Clock,
    accent: '#A855F7',
    description: 'Operations management and workflow automation platform for the modern lending pipeline.',
    tagline: 'Intelligent Operations',
    eta: 'Q2 2026',
    progress: 72,
    milestones: [
      { label: 'Architecture & Data Model', done: true },
      { label: 'Workflow Engine Core', done: true },
      { label: 'Task Scheduling System', done: true },
      { label: 'Performance Monitoring', done: false },
      { label: 'Portal Integration', done: false },
    ],
    capabilities: ['Workflow automation', 'Task scheduling & tracking', 'Performance monitoring', 'Resource optimization'],
  },
  Foundation: {
    icon: Database,
    accent: '#F97316',
    description: 'SAGE, VERDICT, and MTL systems providing foundational AI services powering the entire platform.',
    tagline: 'Core Platform Services',
    eta: 'Q3 2026',
    progress: 58,
    milestones: [
      { label: 'SAGE Compliance Engine', done: true },
      { label: 'VERDICT Risk Detection', done: true },
      { label: 'MTL Milestone Tracking', done: false },
      { label: 'Unified Data Layer', done: false },
      { label: 'Portal Integration', done: false },
    ],
    capabilities: ['SAGE compliance monitoring', 'VERDICT risk detection', 'MTL milestone tracking', 'Unified data platform'],
  },
  Analytics: {
    icon: TrendingUp,
    accent: '#06B6D4',
    description: 'Advanced analytics and business intelligence with AI-powered insights and custom dashboards.',
    tagline: 'Business Intelligence',
    eta: 'Q3 2026',
    progress: 45,
    milestones: [
      { label: 'Data Pipeline Architecture', done: true },
      { label: 'Friction Index Engine', done: true },
      { label: 'CORONER Operational Review', done: false },
      { label: 'Custom Dashboard Builder', done: false },
      { label: 'Portal Integration', done: false },
    ],
    capabilities: ['Friction Index analysis', 'CORONER operational review', 'FALLOUT risk prediction', 'Custom dashboard builder'],
  },
  Channels: {
    icon: Layers,
    accent: '#EC4899',
    description: 'Orion wholesale and retail channel management with AI-powered optimization and partner tools.',
    tagline: 'Channel Management',
    eta: 'Q4 2026',
    progress: 30,
    milestones: [
      { label: 'Channel Data Model', done: true },
      { label: 'Partner Onboarding Flow', done: false },
      { label: 'Revenue Optimization Engine', done: false },
      { label: 'Multi-channel Support', done: false },
      { label: 'Portal Integration', done: false },
    ],
    capabilities: ['Channel performance tracking', 'Automated partner onboarding', 'Revenue optimization', 'Multi-channel support'],
  },
  Admin: {
    icon: Settings,
    accent: '#EF4444',
    description: 'System administration, user management, security configuration, and audit logging.',
    tagline: 'System Administration',
    eta: 'Q2 2026',
    progress: 65,
    milestones: [
      { label: 'User Management', done: true },
      { label: 'Role-Based Access Control', done: true },
      { label: 'System Configuration', done: true },
      { label: 'Audit & Logging', done: false },
      { label: 'Portal Integration', done: false },
    ],
    capabilities: ['User management', 'Role-based access control', 'System configuration', 'Audit logging & compliance'],
  },
};

const ComingSoon: React.FC<ComingSoonProps> = ({ module }) => {
  const config = modules[module];
  
  if (!config) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <p className="text-text-muted">Module not found</p>
      </div>
    );
  }

  const IconComponent = config.icon;
  const doneCount = config.milestones.filter(m => m.done).length;

  return (
    <div className="page-transition active p-6 lg:p-8 max-w-4xl mx-auto">
      {/* Hero */}
      <div className="text-center mb-10 pt-8">
        <div 
          className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
          style={{ backgroundColor: `${config.accent}15`, border: `1px solid ${config.accent}30` }}
        >
          <IconComponent size={36} style={{ color: config.accent }} />
        </div>
        
        <p className="text-[11px] uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: config.accent }}>
          {config.tagline}
        </p>
        <h1 className="text-3xl font-bold text-text-heading mb-3">{module}</h1>
        <p className="text-sm text-text-muted max-w-xl mx-auto leading-relaxed">
          {config.description}
        </p>
      </div>

      {/* Progress Card */}
      <div className="bg-navy-card border border-navy-border rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Rocket size={16} style={{ color: config.accent }} />
            <h3 className="text-sm font-semibold text-text-heading">Development Progress</h3>
          </div>
          <div className="flex items-center space-x-1.5 text-xs text-text-muted">
            <Calendar size={13} />
            <span>Target: {config.eta}</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-text-muted">{doneCount} of {config.milestones.length} milestones</span>
            <span className="text-xs font-semibold" style={{ color: config.accent }}>{config.progress}%</span>
          </div>
          <div className="h-2 bg-navy-border/50 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${config.progress}%`, backgroundColor: config.accent }}
            />
          </div>
        </div>

        {/* Milestones */}
        <div className="space-y-3">
          {config.milestones.map((milestone, i) => (
            <div key={i} className="flex items-center space-x-3">
              {milestone.done ? (
                <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
              ) : (
                <Circle size={16} className="text-navy-border flex-shrink-0" />
              )}
              <span className={`text-sm ${milestone.done ? 'text-text-heading' : 'text-text-muted'}`}>
                {milestone.label}
              </span>
              {milestone.done && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-medium">
                  Complete
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Capabilities */}
      <div className="bg-navy-card border border-navy-border rounded-xl p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-heading mb-4">Planned Capabilities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {config.capabilities.map((cap, i) => (
            <div key={i} className="flex items-center space-x-3 p-3 bg-navy-primary/50 rounded-lg border border-navy-border/50">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: config.accent }} />
              <span className="text-sm text-text-primary">{cap}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center py-6">
        <p className="text-xs text-text-muted mb-4">
          {module} is under active development. We'll integrate it into the portal when ready.
        </p>
        <button 
          className="px-5 py-2.5 rounded-lg text-sm font-semibold border transition-all duration-200 hover:scale-[1.02]"
          style={{ 
            borderColor: `${config.accent}40`, 
            color: config.accent,
            backgroundColor: `${config.accent}08`
          }}
        >
          Notify Me When Ready
        </button>
      </div>
    </div>
  );
};

export default ComingSoon;
